import React, { useState } from 'react'
import ProcessCard from './ProcessCard'
import FeedbackCard from './FeedbackCard'
import { FaCar, FaCreditCard, FaIdCard } from 'react-icons/fa'
import { GrSecure } from 'react-icons/gr'
import { RiSecurePaymentFill } from 'react-icons/ri'
import { TiFlashOutline } from 'react-icons/ti'
import { Link } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'

const SiteBody = () => {
  const [openFaq, setOpenFaq] = useState(0);

  const faqs = [
    {
      q: 'What services can I apply for on RTO Bharat?',
      a: 'You can apply for driving licence support, vehicle-related services, document guidance, and payment-enabled RTO workflows from one dashboard.',
    },
    {
      q: 'How long does document verification take?',
      a: 'Verification timelines depend on service type and document quality. Most applications are reviewed quickly once all required files are submitted correctly.',
    },
    {
      q: 'Is online payment secure on this platform?',
      a: 'Yes. Payments are routed through secure gateways and your transaction details are handled with protected processing standards.',
    },
    {
      q: 'Can I track my application status after payment?',
      a: 'Yes. You can check your current status from My Applications and receive updates as your request progresses.',
    },
  ];

  return (
    <div className='px-4'>
      {/* Our Process */}
      <section className='mt-8 rounded-3xl bg-gradient-to-b from-slate-50 to-white px-5 py-12 md:px-10 shadow-sm'>
        <div className='text-center'>
          <p className='text-sm font-medium tracking-[0.2em] uppercase text-sky-600'>How It Works</p>
          <h3 className='mt-2 text-4xl font-extrabold text-slate-900'>
            Our Process<span className='text-sky-500'>.</span>
          </h3>
          <p className='mt-3 text-slate-500 max-w-2xl mx-auto'>
            Complete your RTO service in three guided digital steps with secure processing and live tracking.
          </p>
        </div>

        <div className='relative mt-14'>
          <div className='hidden md:block absolute left-[16%] right-[16%] top-[62px] pointer-events-none'>
            <svg viewBox='0 0 1200 180' className='w-full h-[130px]'>
              <path
                d='M0,90 C130,150 270,150 400,90 C530,30 670,30 800,90 C930,150 1070,150 1200,90'
                fill='none'
                stroke='#1d8cf8'
                strokeWidth='4'
                strokeDasharray='14 12'
                strokeLinecap='round'
              />
            </svg>
          </div>

          <div className='hidden md:grid grid-cols-3 gap-6'>
            <div className='relative text-center'>
              <p className='text-slate-300 text-5xl font-extrabold'>1</p>
              <div className='mx-auto mt-2 w-24 h-24 rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center shadow-lg shadow-emerald-100/70 z-10 relative'>
                <FaCar className='text-3xl text-emerald-600' />
              </div>
              <h4 className='mt-6 text-2xl font-bold text-slate-900'>Apply Online</h4>
              <p className='mt-3 text-slate-500'>
                Fill out your service form in minutes using our simple online process.
              </p>
            </div>

            <div className='relative text-center'>
              <p className='text-slate-300 text-5xl font-extrabold'>2</p>
              <div className='mx-auto mt-2 w-24 h-24 rounded-full bg-sky-50 border border-sky-100 flex items-center justify-center shadow-lg shadow-sky-100/70 z-10 relative'>
                <FaIdCard className='text-3xl text-sky-600' />
              </div>
              <h4 className='mt-6 text-2xl font-bold text-slate-900'>Verify Documents</h4>
              <p className='mt-3 text-slate-500'>
                Upload required documents and our team validates everything quickly.
              </p>
            </div>

            <div className='relative text-center'>
              <p className='text-slate-300 text-5xl font-extrabold'>3</p>
              <div className='mx-auto mt-2 w-24 h-24 rounded-full bg-cyan-50 border border-cyan-100 flex items-center justify-center shadow-lg shadow-cyan-100/70 z-10 relative'>
                <FaCreditCard className='text-3xl text-cyan-600' />
              </div>
              <h4 className='mt-6 text-2xl font-bold text-slate-900'>Secure Payment</h4>
              <p className='mt-3 text-slate-500'>
                Pay safely through our trusted gateway and track your application status.
              </p>
            </div>
          </div>

          <div className='md:hidden mt-2 space-y-10'>
            <div className='grid grid-cols-[1fr_48px_1fr] items-center gap-3'>
              <div className='text-right pr-2'>
                <p className='text-slate-300 text-4xl font-extrabold'>1</p>
                <h4 className='mt-2 text-2xl font-bold text-slate-900'>Apply Online</h4>
                <p className='mt-2 text-slate-500'>
                  Fill out your service form in minutes using our simple online process.
                </p>
              </div>
              <div className='relative h-full flex justify-center'>
                <div className='w-12 h-12 rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center shadow-md z-10'>
                  <FaCar className='text-xl text-emerald-600' />
                </div>
                <div className='absolute top-12 bottom-[-44px] border-l-2 border-dashed border-sky-300' />
              </div>
              <div />
            </div>

            <div className='grid grid-cols-[1fr_48px_1fr] items-center gap-3'>
              <div />
              <div className='relative h-full flex justify-center'>
                <div className='w-12 h-12 rounded-full bg-sky-50 border border-sky-100 flex items-center justify-center shadow-md z-10'>
                  <FaIdCard className='text-xl text-sky-600' />
                </div>
                <div className='absolute top-12 bottom-[-44px] border-l-2 border-dashed border-sky-300' />
              </div>
              <div className='text-left pl-2'>
                <p className='text-slate-300 text-4xl font-extrabold'>2</p>
                <h4 className='mt-2 text-2xl font-bold text-slate-900'>Verify Documents</h4>
                <p className='mt-2 text-slate-500'>
                  Upload required documents and our team validates everything quickly.
                </p>
              </div>
            </div>

            <div className='grid grid-cols-[1fr_48px_1fr] items-center gap-3'>
              <div className='text-right pr-2'>
                <p className='text-slate-300 text-4xl font-extrabold'>3</p>
                <h4 className='mt-2 text-2xl font-bold text-slate-900'>Secure Payment</h4>
                <p className='mt-2 text-slate-500'>
                  Pay safely through our trusted gateway and track your application status.
                </p>
              </div>
              <div className='relative h-full flex justify-center'>
                <div className='w-12 h-12 rounded-full bg-cyan-50 border border-cyan-100 flex items-center justify-center shadow-md z-10'>
                  <FaCreditCard className='text-xl text-cyan-600' />
                </div>
              </div>
              <div />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose RTO Bharat ? */}
      <div className='mt-8 text-center'>
        <h3 className='text-2xl font-semibold text-gray-800 dark:text-white tracking-wide'>
          Why Choose RTO Bharat ?
        </h3>
        <div className='mt-3 w-[200px] h-1 bg-cyan-500 mx-auto rounded'></div>
        <h2 className='mt-2 font-semibold'>
          RTO Bharat is your trusted partner for all digital transport services in India.
        </h2>
        {/* <h2 className='font-semibold'>
          We provide a seamless experience with quick processing, secure payments, and government-authorized services.
        </h2> */}
      </div>

      <div className='grid gap-5 p-8 md:grid-cols-3'>
        <ProcessCard
          title='100% Digital & Secure'
          content='All services are processed through secure and government-approved channels.'
          lcolor='text-cyan-600'
          icon={GrSecure}
        />
        <ProcessCard
          title='Fast Processing'
          content='Get your work done faster with our easy-to-use online platform.'
          lcolor='text-cyan-600'
          icon={TiFlashOutline}
        />
        <ProcessCard
          title='Secure Payments'
          content='Make hassle-free payments using Razorpay’s secure gateway.'
          lcolor='text-cyan-600'
          icon={RiSecurePaymentFill}
        />
      </div>

      {/* What Our Customer Say */}
      <div className='mt-8 text-center'>
        <h3 className='text-2xl font-semibold text-gray-800 dark:text-white tracking-wide'>
          What Our Customers Say
        </h3>
        <div className='mt-3 w-[200px] h-1 bg-yellow-500 mx-auto rounded'></div>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-4 gap-5 p-8'>
        <FeedbackCard
          title='- Anil Kumar'
          content='"RTO Bharat made my vehicle registration process so easy and quick. Highly recomended their services!"'
        />
        <FeedbackCard
          title='- Priya Sharma'
          content='"I was able to renew my driving license without any hassle. Great Service!"'
        />
        <FeedbackCard
          title='- Rajesh Singh'
          content='"The best platform for all RTO related service. Very user-friendly and efficient"'
        />
        <FeedbackCard
          title='- Arindam Saha'
          content='"The reporting features are comprehensive and helpful and the pricing seems competitive."'
        />
      </div>

      {/* About Us */}
      <div className='mt-8 text-center'>
        <h3 className='text-2xl font-semibold text-gray-800 dark:text-white tracking-wide'>
          <Link to='/about' className='hover:text-blue-500'>
            About Us
          </Link>
        </h3>
        <div className='mt-3 w-[70px] h-1 bg-red-500 mx-auto rounded'></div>
        <h2 className='mx-16 my-8 font-semibold'>
          RTO-BHARAT is a digital service platform by MOTOMITRA (OPC) PRIVATE LIMITED, assisting individuals and vehicle owners with online application support and documentation guidance for RTO and transport services.
        </h2>
         <h2 className='font-semibold'>
          Our offerings include help with form filling, document preparation, submission assistance, and status updates for driving licenses, vehicle registration, and challan processes.<br/> As an independent service provider, RTO-BHARAT simplifies the application process without representing any government authority.
        </h2> 
      </div>

      {/* FAQ Section */}
      <section className='mt-8 rounded-3xl bg-gradient-to-b from-slate-50 to-white p-6 md:p-10 shadow-sm border border-slate-200'>
        <div className='text-center'>
          <p className='inline-block px-3 py-1 rounded-full bg-[#e6f2ff] text-[10px] md:text-xs font-semibold tracking-[0.18em] uppercase text-[#2d6fb7]'>
            RTO BHARAT SUPPORT
          </p>
          <h3 className='mt-3 text-2xl md:text-4xl font-bold tracking-tight text-[#1f376d]'>
            Frequently Asked Questions
          </h3>
          <p className='mt-3 text-sm md:text-base text-slate-600 max-w-2xl mx-auto'>
            Common questions about our services, process, and payments.
          </p>
        </div>

        <div className='mt-8 space-y-3 max-w-5xl mx-auto'>
          {faqs.map((item, idx) => {
            const isOpen = openFaq === idx;
            return (
              <div
                key={item.q}
                className='rounded-xl bg-[#edf6ff] text-slate-900 border border-[#d6e9ff] overflow-hidden'
              >
                <button
                  type='button'
                  onClick={() => setOpenFaq(isOpen ? -1 : idx)}
                  className='w-full flex items-center justify-between gap-3 px-4 md:px-5 py-4 text-left border-l-[3px] border-[#2d8cff]'
                >
                  <span className='font-semibold text-[0.95rem] md:text-[1.2rem] leading-tight text-[#1f376d]'>
                    {item.q}
                  </span>
                  <span className='flex-shrink-0 w-6 h-6 rounded-full bg-[#2d8cff] text-white flex items-center justify-center'>
                    <ChevronDown
                      size={14}
                      className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : 'rotate-0'}`}
                    />
                  </span>
                </button>
                {isOpen && (
                  <div className='px-5 pb-4 text-slate-600 text-xs md:text-sm leading-6 border-l-[3px] border-[#2d8cff]'>
                    {item.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>


      {/* <div className='grid grid-cols-1 md:grid-cols-2 gap-5 p-8'>
        <ProcessCard
          title='Our Vision'
          content='Our vision is to become a trusted digital support platform for RTO application assistance by making transport-related procedures easier, more transparent, and accessible to everyone.'
          lcolor='text-red-500'
          icon={FaEye}
        />
        <ProcessCard
          title='Our Mission'
          content='RTO-BHARAT simplifies RTO applications for individuals and vehicle owners in India, offering reliable online support to save time and ensure smooth procedures.'
          lcolor='text-red-500'
          icon={TbTargetArrow}
        />
      </div> */}
    </div>
  )
}

export default SiteBody
